import * as React from 'react';
import cx from 'classnames';
import Icon, { IconName } from 'components/icon/icon';
import searchFieldLexemes from 'components/lexemes/search-field';
import { useRouter } from 'next/router';
import LocaleContext from 'lib/locale-context';
import { useContext } from 'react';

type Props = {
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    sizeSmall?: boolean;
    isFullWidth?: boolean;
    hasContrast?: boolean;
    isMobile?: boolean;
};

const SearchField: React.FC<Props> = ({
    onChange,
    sizeSmall,
    isFullWidth,
    hasContrast,
    isMobile,
}) => {
    const [inputValue, setValue] = React.useState('');
    const [isFocused, setFocus] = React.useState(false);
    const router = useRouter();
    const lang = useContext(LocaleContext);
    const inputRef: React.RefObject<HTMLInputElement> = React.useRef<HTMLInputElement>(null);

    const classname = cx({
        'search-field': true,
        'is-focused': isFocused || inputValue !== '',
        'has-value': inputValue,
        'search-field--sm': sizeSmall,
        'is-full-width': isFullWidth,
        'has-contrast': hasContrast,
        'is-mobile': isMobile,
    });

    const onSearchSubmitHandler = (event: React.SyntheticEvent) => {
        event.preventDefault();

        if (inputValue === '') {
            return;
        }

        /**
         * Fix for search query with several words - API needs +
         */
        const searchQuery = encodeURIComponent(inputValue.replace(/ /g, '+'));
        const route = `/${lang}/search-results?searchQuery=${searchQuery}`;
        router.push(route);
    };

    return (
        <form
            className={classname}
            onSubmit={onSearchSubmitHandler}
            onFocus={() => setFocus(true)}
            role="search"
        >
            <label className="search-field__inner" htmlFor="search">
                <span className="search-field__label" aria-hidden={true}>
                    {searchFieldLexemes.lxPlaceholderTitle()}
                </span>

                <input
                    className="search-field__input"
                    id="search"
                    type="search"
                    autoComplete="off"
                    autoCorrect="off"
                    inputMode="search"
                    size={1}
                    value={inputValue}
                    onFocus={() => setFocus(true)}
                    onBlur={() => setFocus(false)}
                    onChange={(event) => {
                        if (onChange) {
                            onChange(event);
                        }
                        setValue(event.target.value);
                    }}
                    aria-label={searchFieldLexemes.lxPlaceholderTitle()}
                    ref={inputRef}
                />
            </label>

            <button
                className="search-field__icon"
                type="submit"
                aria-label={searchFieldLexemes.lxSearchA11yLabel()}
            >
                <Icon name={IconName.SearchBold} />
            </button>

            <button
                className="search-field__clear"
                onClick={() => {
                    const input: HTMLInputElement | null = inputRef.current;
                    setValue('');
                    if (input) {
                        input.focus();
                    }
                }}
                type="reset"
                tabIndex={inputValue ? 0 : -1}
                aria-hidden={!inputValue}
                aria-label={searchFieldLexemes.lxClearSearchA11yLabel()}
            >
                <Icon name={IconName.Close} size="stretch" />
            </button>
        </form>
    );
};

export default SearchField;
